'use client';

import * as React from 'react';
import { DuckDBEngine, TableSourceType, TableFormat, TableRef } from '../../lib/engine';
import { QueryType, SQLDialect } from '../../lib/engine/core/types';
import { IndexedDBFileStore, FileHandle } from '../../lib/engine/core/file-store';
import { toast } from '../ui/toast';
import { AppEmptyState } from '../app-empty-state';
import { DataframeSource, registerDataframe } from '../../lib/register-dataframe';
import { UpgradeModal } from '../subscription/upgrade-modal';
import { trackAction } from '../../lib/tracking';
import { createClient } from '../../lib/supabase/client';
import { TableViewerNova } from '../nova';
import { DatasetSkeleton } from '../dataset/dataset-skeleton';
import { useSubscription } from '../../hooks/use-subscription';

export interface AppClientComponentProps {
  initialPrompt?: string | undefined;
  fileId?: string | undefined;
  sampleId?: string | undefined;
  source?: string | undefined;
}

export default function AppClientComponent({ initialPrompt, fileId, sampleId, source }: AppClientComponentProps) {
  // Separate engine and file loading states for better UX
  const [initState, setInitState] = React.useState<{
    isEngineReady: boolean;
    engine: DuckDBEngine | null;
    engineError: Error | null;
    isFileLoaded: boolean;
    activeTable: TableRef | null;
    fileLoadingStatus: 'idle' | 'loading' | 'success' | 'error';
  }>({
    isEngineReady: false,
    engine: null,
    engineError: null,
    isFileLoaded: false,
    activeTable: null,
    fileLoadingStatus: 'idle'
  });

  // Get subscription status
  const { hasSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  // State for upgrade modal
  const [upgradeModal, setUpgradeModal] = React.useState<{
    openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'create_dataset'
  } | null>(null);

  // Background engine initialization - only when needed
  React.useEffect(() => {
    // Case 1: No query params - don't initialize engine until user uploads
    if (!fileId && !sampleId) {
      console.log('No query params - engine will be initialized on demand');
      return;
    }

    // Case 2: Has query params - need engine to load data
    let isMounted = true;
    let currentEngine: DuckDBEngine | null = null;

    const initializeEngine = async () => {
      try {
        console.log('Starting background engine initialization for query params...');
        
        // Create engine in background
        const dbEngine = await DuckDBEngine.create();
        currentEngine = dbEngine;
        
        if (!isMounted) {
          dbEngine.dispose();
          return;
        }

        // Engine is ready - update state
        setInitState(prev => ({
          ...prev,
          engine: dbEngine,
          isEngineReady: true,
          engineError: null
        }));
        
        console.log('Engine ready in background');
      } catch (error) {
        console.error('Error initializing engine in background:', error);
        if (!isMounted) return;
        
        setInitState(prev => ({
          ...prev,
          engineError: error instanceof Error ? error : new Error(String(error)),
          isEngineReady: false
        }));
      }
    };

    initializeEngine();

    return () => {
      isMounted = false;
      if (currentEngine) {
        currentEngine.dispose();
      }
    };
  }, [fileId, sampleId]); // Only run when query params change

  // Handle file/sample loading when engine becomes ready
  React.useEffect(() => {
    if (!initState.isEngineReady || !initState.engine) return;
    if (initState.fileLoadingStatus !== 'idle') return; // Prevent re-runs
    if (!fileId && !sampleId) return; // Nothing to load

    let isMounted = true;

    const loadFileOrSample = async () => {
      const dbEngine = initState.engine!;
      
      try {
        setInitState(prev => ({ ...prev, fileLoadingStatus: 'loading' }));

        // Handle sampleId
        if (sampleId) {
          console.log(`Loading sample dataset with ID: ${sampleId}`);
          
          const dataset = await import('../../lib/demo-datasets').then(mod => mod.getDemoDataset(sampleId as any));
          if (!dataset) {
            throw new Error(`Sample dataset not found: ${sampleId}`);
          }
          
          const datasetUrl = dataset.url.startsWith('http') ? 
            dataset.url : 
            new URL(dataset.url, window.location.origin).toString();
            
          const response = await fetch(datasetUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch sample dataset: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          const format = await import('../../lib/formats').then(mod => mod.Format.PARQUET);
          const mimeTypes = await import('../../lib/formats').then(mod => mod.FORMAT_MIME_TYPES);
          
          const datasetFile = new File([blob], `${dataset.id}.parquet`, {
            type: mimeTypes[format]
          });
          
          const DataframeSource = await import('../../lib/register-dataframe').then(mod => mod.DataframeSource);
          const registerDataframe = await import('../../lib/register-dataframe').then(mod => mod.registerDataframe);
          
          const table = await registerDataframe({
            file: datasetFile,
            engine: dbEngine,
            source: DataframeSource.EXAMPLE,
            options: {
              path: dataset.id,
              upsert: true
            }
          });
          
          if (!table) {
            throw new Error(`Sample dataset registration returned no table reference`);
          }
          
          const tables = await dbEngine.listRegisteredTables();
          const registeredTable = tables.find(t => t.id === table.id || t.path === table.path);
          if (!registeredTable) {
            throw new Error(`Table not found in engine after registration`);
          }

          if (!isMounted) return;
          
          setInitState(prev => ({
            ...prev,
            isFileLoaded: true,
            activeTable: registeredTable,
            fileLoadingStatus: 'success'
          }));
          
          trackAction('app_sample_loaded', {
            sample_id: sampleId,
            sample_name: dataset.name
          });
        }
        // Handle fileId
        else if (fileId) {
          console.log(`Loading file from IndexedDB with ID: ${fileId}`);
          
          const fileStore = new IndexedDBFileStore();
          const fileHandle = await fileStore.getFileHandle(fileId);
          
          if (fileHandle && fileHandle.handle) {
            // Get source from URL parameter (cleaner than checking referrer/history)
            const urlSource = source;
            const isFromDesktop = urlSource === 'desktop';
            
            // Use registerDataframe for proper tracking instead of direct registerFromFile
            const tableRef = await registerDataframe({
              file: fileHandle.handle,
              engine: dbEngine,
              source: isFromDesktop ? DataframeSource.OPEN_FROM_DESKTOP : DataframeSource.UPLOAD,
              options: {
                path: fileHandle.handle.name,
                upsert: true
              }
            });

            if (!isMounted) return;
            
            setInitState(prev => ({
              ...prev,
              isFileLoaded: true,
              activeTable: tableRef,
              fileLoadingStatus: 'success'
            }));
            
            // Keep existing tracking for backward compatibility
            trackAction('app_file_loaded_from_indexdb', {
              file_id: fileId,
              file_name: fileHandle.handle.name,
              file_size: fileHandle.handle.size,
              file_type: fileHandle.handle.type || 'unknown'
            });
          } else {
            // No file found - check for existing tables
            const tables = await dbEngine.listRegisteredTables();
            if (tables.length > 0) {
              setInitState(prev => ({
                ...prev,
                isFileLoaded: true,
                activeTable: tables[0],
                fileLoadingStatus: 'success'
              }));
            } else {
              setInitState(prev => ({
                ...prev,
                fileLoadingStatus: 'success'
              }));
            }
          }
          
          await fileStore.close();
        }
      } catch (error) {
        console.error('Error loading file/sample:', error);
        if (!isMounted) return;
        
        setInitState(prev => ({
          ...prev,
          fileLoadingStatus: 'error'
        }));
        
        toast.error(`Error loading data: ${error instanceof Error ? error.message : String(error)}`);
        
        // Try to restore from previous session
        checkPreviousSession(dbEngine);
      }
    };

    loadFileOrSample();

    return () => {
      isMounted = false;
    };
  }, [initState.isEngineReady, fileId, sampleId]); // React to engine readiness
  
  // Helper function to check for previous session data
  const checkPreviousSession = async (dbEngine: DuckDBEngine) => {
    // Let's try to find any tables that might already be registered
    try {
      const tables = await dbEngine.listRegisteredTables();
      console.log(`Found ${tables.length} tables already registered in engine`);
      
      if (tables.length > 0) {
        // Use the first table as our active table
        console.log(`Using first registered table: ${tables[0].id}`);
        setInitState(prev => ({
          ...prev,
          isFileLoaded: true,
          activeTable: tables[0]
        }));
      } else {
        setInitState(prev => ({
          ...prev,
          isFileLoaded: false,
          activeTable: null
        }));
      }
    } catch (err) {
      console.error('Error checking for registered tables:', err);
      setInitState(prev => ({
        ...prev,
        isFileLoaded: false,
        activeTable: null
      }));
    }
  };
  
  // Handle files selected for upload
  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    
    try {
      const file = files[0];
      console.log(`Storing file in IndexedDB: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
      
      // Generate a unique file ID
      const fileId = `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      // Store file in IndexedDB using the correct API
      const fileStore = new IndexedDBFileStore();
      await fileStore.storeFileHandle(fileId, {
        dataTableSource: fileId,
        handle: file
      });
      await fileStore.close();
      
      console.log(`File stored with ID: ${fileId}`);
      
      // Redirect to /app with file-id parameter
      const url = new URL(window.location.href);
      url.searchParams.set('file-id', fileId);
      window.location.href = url.toString();
      
    } catch (err) {
      console.error('Error storing file:', err);
      toast.error(`Error uploading file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
  
  // Handle upgrade clicks
  const handleUpgradeClick = (args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'create_dataset' }) => {
    setUpgradeModal(args);
    
    // Track the event
    trackAction('upgrade_modal_triggered', {
      source: 'app_page',
      trigger: args.openTrigger
    });
  };
  
  // Show error if initialization failed
  if (initState.engineError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <h2 className="text-xl font-semibold mb-4 text-destructive">Failed to initialize engine</h2>
        <p className="mb-4 text-center max-w-lg">{initState.engineError?.message || 'Unknown error'}</p>
        <button 
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
  
  // Show TableViewerNova if we have engine + file loaded + columns ready
  if (initState.isEngineReady && initState.engine && initState.isFileLoaded && initState.activeTable) {
    return (
      <>
        <div className="w-full h-full flex overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            <TableViewerNova 
              engine={initState.engine} 
              activeTable={initState.activeTable}
              className="flex-1 !border-none !rounded-none max-h-full"
              initialPrompt={initialPrompt}
              subscriptionInfo={{
                hasActiveSubscription: isSubscriptionLoading ? true : hasSubscription
              }}
              onUpgradeClick={handleUpgradeClick}
            />
          </div>
        </div>
        <UpgradeModal
          open={upgradeModal !== null}
          onClose={() => setUpgradeModal(null)}
          openTrigger={upgradeModal?.openTrigger}
        />
      </>
    );
  }
  
  // Case 2: Has query params - show loading skeleton while engine/file loads
  if (fileId || sampleId) {
    const getLoadingMessage = () => {
      if (!initState.isEngineReady) {
        return 'Preparing data engine...';
      }
      if (initState.fileLoadingStatus === 'loading') {
        return sampleId ? 'Loading sample dataset...' : 'Loading your file...';
      }
      return 'Loading...';
    };

    return (
      <>
        <DatasetSkeleton 
          datasetName={getLoadingMessage()}
        />
        <UpgradeModal
          open={upgradeModal !== null}
          onClose={() => setUpgradeModal(null)}
          openTrigger={upgradeModal?.openTrigger}
        />
      </>
    );
  }
  
  // Case 1: No query params - show upload interface
  return (
    <>
      <AppEmptyState 
        onFilesSelected={handleFilesSelected}
      />
      <UpgradeModal
        open={upgradeModal !== null}
        onClose={() => setUpgradeModal(null)}
        openTrigger={upgradeModal?.openTrigger}
      />
    </>
  );
} 